/*
 * Grouped/Bundle "Quick start" (2026-10-09, first case: Netafim Drip System
 * Creator). Renders below the image gallery (same Custom Liquid slot as the
 * "Your System" hero) when the wrapper's config carries a `quickStart`
 * block. The customer describes their grow; this suggests a full build and,
 * on "Use this build", hands it to the buy-box builder through the
 * "hbi:groupedset" event (ui/grouped-ui.js) - the two blocks never touch
 * each other's DOM, same decoupling as the hero.
 *
 * SKUs are never hardcoded here: every line is looked up in the config by
 * item key + option values (e.g. group "asm" at { flow: "0.5", len: "24" }),
 * so the merchandiser-owned config stays the single source of products.
 * Prices are read live from /products/<handle>.js, never stored.
 *
 * quickStart.type "drip" is the only planner so far. Suggestions are typical
 * starting points (see tools/gen_netafim_config.py), never requirements.
 */
(function () {
  'use strict';

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function money(cents) {
    if (typeof cents !== 'number' || !isFinite(cents)) return '';
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function ceil(a, b) { return Math.ceil(a / b); }

  /* ── config lookups ── */
  function indexItems(config) {
    var byKey = {};
    (config.sections || []).forEach(function (s) {
      (Array.isArray(s.items) ? s.items : []).forEach(function (it) { if (it.key) byKey[it.key] = { item: it, section: s.key }; });
    });
    return byKey;
  }
  // -> { sku, handle, name, groupKey?, values? } or null
  function pick(byKey, key, values) {
    var hit = byKey[key];
    if (!hit) return null;
    var it = hit.item;
    if (!Array.isArray(it.variants)) return { sku: it.sku, handle: it.handle, name: it.name, section: hit.section, pack: it.pack || 1, unit: it.unit };
    var v = null;
    for (var i = 0; i < it.variants.length && !v; i++) {
      var ok = true;
      for (var k in values) if (it.variants[i].values[k] !== values[k]) ok = false;
      if (ok) v = it.variants[i];
    }
    if (!v) return null;
    var label = it.options.map(function (o) {
      var val = o.values.filter(function (x) { return x.key === v.values[o.key]; })[0];
      return val ? val.label : '';
    }).filter(Boolean).join(' · ');
    return { sku: v.sku, handle: v.handle, name: it.name, detail: label, groupKey: it.key, values: v.values, section: hit.section, tags: v.tags || [], pack: v.pack || it.pack || 1, unit: it.unit };
  }

  /* ── drip planner ── */
  var STYLE_LABEL = { asm: 'Ready-made stakes', mod: 'MOD manifolds', netbow: 'NetBow rings' };
  var GPH = { '0.5': 0.53, '1.06': 1.06, '2.11': 2.11 };

  // Plain-text build summary for a mailto link next to "Use this build" -
  // no recipient is ever filled in here. This lets the shopper (or an
  // HBI employee helping them) address it to whoever they want to loop
  // in - their own team, a colleague, anyone - rather than assuming a
  // single fixed destination (Dustin, 2026-10-14).
  function buildMailto(config, P, prices, total, priced) {
    var lines = ['Drip system build: ' + (config.family && config.family.label || 'Drip system'), '',
      'Container: ' + P.c.label, 'Plants: ' + P.plants, 'Drippers per plant: ' + P.points,
      'Rows / benches: ' + P.rows, 'Bench width: ' + P.benchWidth + ' ft', 'Row length: ' + P.rowLength + ' ft'];
    if (P.zones > 1) lines.push('Zones: ' + P.zones);
    lines.push('');
    lines.push('Parts:');
    P.lines.forEach(function (l) {
      var price = prices[l.r.sku], pk = l.r.pack || 1;
      var qty = (pk > 1 ? l.qty + (l.qty === 1 ? ' pack' : ' packs') : l.qty + ' x');
      lines.push('- ' + qty + ' ' + l.r.name + (l.r.detail ? ' - ' + l.r.detail : '') + (typeof price === 'number' ? ' (' + money(price * l.qty) + ')' : ''));
    });
    lines.push('');
    lines.push(priced && total ? 'Estimated subtotal: ' + money(total) : 'Estimated subtotal: pending (some prices did not load)');
    lines.push('', 'Sent from the Quick Start planner on ' + (typeof window !== 'undefined' ? window.location.href : ''));
    var subject = (config.family && config.family.label || 'Drip system') + ' build';
    return 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(lines.join('\n'));
  }

  function plural(word, n) {
    if (n === 1) return word;
    if (/(s|x|ch|sh)$/.test(word)) return word + 'es';
    if (/[^aeiou]y$/.test(word)) return word.slice(0, -1) + 'ies';
    return word + 's';
  }
  function spare(n, pack) { var x = ceil(n, pack) * pack - n; return x > 0 ? ' · ' + x + ' spare' : ''; }
  // Zones (2026-10-15, per docs/status-2026-10-14-netafim-planner-next-plan.md
  // #1 - simplified same day from a zones x rooms pair to one field: the two
  // fields multiplied identically with no functional difference, and Dustin
  // found the distinction confusing even knowing the product, so a customer
  // would too - "repeat everything above N times" needs exactly one number).
  // Every per-zone part's RAW (unrounded) need is summed across all zones and
  // rounded to pack ONCE here, in add() - never per-zone - or a multi-zone
  // project silently over-orders (the #76 tubing bug: re-rounding per zone
  // instead of summing raw first inflated one real order 76x). Project-wide
  // parts (punch tool, goof plugs) stay x1 regardless - pass
  // projectWide=true for those. zones=1 must reproduce today's exact
  // per-line quantities (multiplier collapses to 1), verified below.
  function planDrip(qs, byKey, st) {
    var c = qs.containers.filter(function (x) { return x.key === st.container; })[0] || qs.containers[0];
    var plants = Math.max(1, Math.round(st.plants || 1));
    var rows = Math.max(1, Math.round(st.rows || 1));
    var rowLen = Math.max(1, +st.rowLength || 1);
    var zones = Math.max(1, Math.round(st.zones || 1));
    var style = st.style === 'auto' ? c.style : st.style;
    var benchWidth = Math.max(0, +st.benchWidth || 0);
    var points = Math.max(1, Math.round(st.pointsPerPlant || c.points));
    var len = String(st.tubeLength), lines = [], notes = [], drippers, perPlantGph;
    var zoneMultiplier = zones;
    var scaleSuffix = zoneMultiplier > 1 ? ' (x ' + zoneMultiplier + ' zones)' : '';
    function add(key, values, rawQtyPerZone, pack, why, projectWide) {
      var r = pick(byKey, key, values);
      if (!r || rawQtyPerZone <= 0) return;
      var multiplier = projectWide ? 1 : zoneMultiplier;
      var qty = ceil(rawQtyPerZone * multiplier, pack);
      if (qty <= 0) return;
      lines.push({ r: r, qty: qty, why: why + (!projectWide ? scaleSuffix : '') });
    }

    if (style === 'asm') {
      var a = pick(byKey, 'asm', { flow: '0.5', len: len }) ? len : '24';
      var asm = pick(byKey, 'asm', { flow: '0.5', len: a });
      var pack = asm && asm.tags.indexOf('case') >= 0 ? 375 : 25;
      drippers = plants * points; perPlantGph = points * 0.53;
      add('asm', { flow: '0.5', len: a }, drippers, pack, drippers + ' assemblies needed per zone - ' + points + ' per plant at 0.5 GPH' + spare(drippers * zoneMultiplier, pack) + (pack === 375 ? ' (sold by the case of 375)' : ''));
      if (a !== len) notes.push({ level: 'tip', text: 'Assemblies at 0.5 GPH come in 18-48" tubes, so we used ' + a + '".' });
    } else if (style === 'mod') {
      var ways = points <= 2 ? '2' : '4', per = Math.ceil(points / +ways), nf = ways === '2' ? '1.06' : '2.11';
      var ml = pick(byKey, 'modasm', { ways: ways, len: len }) ? len : '24';
      drippers = plants * per; perPlantGph = per * GPH[nf];
      add('modasm', { ways: ways, len: ml }, drippers, 25, drippers + ' ' + ways + '-way MOD assemblies needed per zone - ' + (per > 1 ? per + ' per plant, ' : '') + (per * +ways) + ' drip points per plant');
      add('nip', { flow: nf, pack: '250' }, drippers, 250, drippers + ' nipple emitters per zone at ' + nf + ' GPH (about ' + (GPH[nf] / +ways).toFixed(2) + ' GPH per drip point) - one per MOD');
      if (ml !== len) notes.push({ level: 'tip', text: ways + '-way MOD assemblies start at 24", so we used 24" tubes.' });
    } else {
      var perB = Math.max(1, Math.ceil(points / 4));
      var tl = pick(byKey, 'mtube', { len: len }) ? len : '24';
      drippers = plants * perB; perPlantGph = perB * 2.11;
      add('netbow', {}, drippers, 120, drippers + ' NetBow rings per zone - ' + perB + ' per plant, 4 outlets each');
      add('barb', { flow: '2.11', pack: '250' }, drippers, 250, drippers + ' barbed hub emitters per zone at 2.11 GPH (about 0.53 GPH per outlet)');
      add('mtube', { len: tl }, drippers, 100, drippers + ' micro tubes per zone, ' + tl + '" - emitter to ring');
      if (c.liters < 9) notes.push({ level: 'tip', text: 'NetBow is designed for 2.5-15 gal containers - for smaller pots, stakes are usually the better fit.' });
    }

    var ft = Math.ceil(rows * rowLen * 1.1 + 10);
    add('lat', { size: '17-500' }, ft, 500, 'About ' + ft + ' ft of 17 mm line per zone for ' + rows + ' row' + (rows > 1 ? 's' : '') + ' (+10% and the header)');
    if (rows > 1) add('fit_tee', {}, rows - 1, 25, (rows - 1) + ' tee' + (rows > 2 ? 's' : '') + ' per zone to branch rows off the header');
    add('fit_ell', {}, 1, 1, 'Elbows for the header corner and the last row, per zone');
    add('fit_ma', {}, 1, 1, "Connects the 17 mm line to the regulator's ¾\" outlet, per zone");
    add('fit_sov', {}, rows, 10, rows + ' shut-off valve' + (rows > 1 ? 's' : '') + ' per zone - one per row to isolate or flush it');
    add('filter', {}, 1, 1, '140-mesh disc filter protects every dripper - one per zone');
    var psi = rowLen > 120 ? '35' : '20';
    add('reg', { psi: psi }, 1, 1, psi + ' PSI keeps every dripper in its working range' + (psi === '35' ? ' on long rows' : '') + ', one regulator per zone');
    add('punch', {}, 1, 1, 'Makes a clean hole for each dripper', true);
    add('goof', {}, 1, 1, 'Seals any misplaced holes', true);

    var zoneGph = plants * perPlantGph, gpm = zoneGph / 60, phone = qs.phone || '';
    if (gpm > 4.4) notes.push({ level: 'warn', text: 'Total flow is about ' + gpm.toFixed(1) + ' GPM per zone - more than one low-flow regulator handles (4.4 GPM). Split into more zones' + (phone ? ', or call ' + phone + ' for a larger regulator.' : '.') });
    if (gpm > 12) notes.push({ level: 'warn', text: "That's also above the ¾\" disc filter's 12 GPM rating per zone - use one filter per zone." });
    if (gpm < 0.25) notes.push({ level: 'tip', text: "Very small systems can run below the regulator's 0.25 GPM minimum flow - it still works, but regulation is less precise." });
    if (zoneGph / rows > 200 || rowLen > 200) notes.push({ level: 'warn', text: 'Each row carries about ' + Math.round(zoneGph / rows) + ' GPH over ' + rowLen + ' ft. Keep 17 mm rows under roughly 200 GPH and 200 ft - add rows or feed long rows from both ends.' });
    var shotMin = c.liters * 0.03 / (perPlantGph * 3.785) * 60;
    return { c: c, style: style, points: points, plants: plants, rows: rows, rowLength: rowLen, benchWidth: benchWidth, zones: zones, lines: lines, notes: notes, perPlantGph: perPlantGph, gpm: gpm, shotMin: shotMin };
  }

  /* ── mount ── */
  function mount(root) {
    var dataEl = document.getElementById(root.getAttribute('data-config-id'));
    if (!dataEl) return;
    var config;
    try { config = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var qs = config.quickStart;
    if (!qs || qs.type !== 'drip' || !Array.isArray(qs.containers) || !qs.containers.length) { root.hidden = true; return; }
    var byKey = indexItems(config);
    var d = qs.defaults || {};
    var startContainer = qs.containers.filter(function (x) { return x.key === (d.container || qs.containers[0].key); })[0] || qs.containers[0];
    var st = { container: startContainer.key, plants: d.plants || 24, rows: d.rows || 1, rowLength: d.rowLength || 20, zones: d.zones || 1, benchWidth: d.benchWidth || 4, tubeLength: d.tubeLength || '24', style: 'auto', pointsPerPlant: d.pointsPerPlant || startContainer.points, pointsPerPlantTouched: false };
    var prices = {}; // sku -> cents | null
    var pending = {};

    // Optional server-rendered "How it works" block inside the root (the
    // full-width Creator page puts one there) - kept and placed under the
    // form, filling the space beside the taller suggestion column.
    var howto = root.querySelector('.hbc-howto');
    if (howto) howto.parentNode.removeChild(howto);
    root.textContent = '';
    var grid = el('div', 'hbi-qs__grid');
    var left = el('div', 'hbi-qs__left');
    var form = el('div', 'hbi-qs__form');
    var out = el('div', 'hbi-qs__out');
    out.setAttribute('aria-live', 'polite');
    left.appendChild(form);
    if (howto) left.appendChild(howto);
    grid.appendChild(left); grid.appendChild(out); root.appendChild(grid);

    function chips(name, opts, cur, onPick) {
      var wrap = el('div', 'hbi-qs__chips');
      opts.forEach(function (o) {
        var b = el('button', 'hbi-qs__chip' + (o.key === cur ? ' hbi-qs__chip--on' : ''), o.label);
        b.type = 'button';
        b.setAttribute('aria-pressed', o.key === cur ? 'true' : 'false');
        b.addEventListener('click', function () { onPick(o.key); });
        wrap.appendChild(b);
      });
      return wrap;
    }
    // hint (2026-10-15, plan part 3): one short, quiet line directly under a
    // field, only passed for fields that actually need explaining - not
    // every field, and not a wall of unrelated tips bunched at the bottom.
    function field(label, child, hint) {
      var f = el('div', 'hbi-qs__field');
      f.appendChild(el('span', 'hbi-qs__label', label));
      f.appendChild(child);
      if (hint) f.appendChild(el('p', 'hbi-qs__field-hint', hint));
      return f;
    }
    function num(key, min, max, touchKey) {
      var i = el('input', 'hbi-qs__input');
      i.type = 'number'; i.min = String(min); i.inputMode = 'numeric'; i.value = st[key];
      i.addEventListener('input', function () {
        var v = parseFloat(i.value);
        if (v >= min) { st[key] = Math.min(v, max); if (touchKey) st[touchKey] = true; renderOut(); }
      });
      return i;
    }

    function renderForm() {
      form.textContent = '';
      var c = qs.containers.filter(function (x) { return x.key === st.container; })[0] || qs.containers[0];
      form.appendChild(field('Container or media', chips('cont', qs.containers, st.container, function (k) {
        st.container = k; st.style = 'auto';
        // Re-recommend drippers/plant for the new container unless the
        // grower already typed their own value (st.pointsPerPlantTouched).
        if (!st.pointsPerPlantTouched) {
          var nc = qs.containers.filter(function (x) { return x.key === k; })[0];
          if (nc) st.pointsPerPlant = nc.points;
        }
        renderForm(); renderOut();
      })));
      var pair = el('div', 'hbi-qs__pair');
      pair.appendChild(field('Plants', num('plants', 1, 100000)));
      pair.appendChild(field('Drippers per plant', num('pointsPerPlant', 1, 50, 'pointsPerPlantTouched'),
        'Defaults to our recommendation for this container - edit freely.'));
      form.appendChild(pair);
      var pair2 = el('div', 'hbi-qs__pair');
      pair2.appendChild(field('Rows / benches', num('rows', 1, 1000)));
      pair2.appendChild(field('Bench width (ft)', num('benchWidth', 1, 200)));
      form.appendChild(pair2);
      var pair3 = el('div', 'hbi-qs__pair');
      pair3.appendChild(field('Row length (ft)', num('rowLength', 1, 2000)));
      pair3.appendChild(field('Tube length', chips('len', ['18', '24', '30', '36', '48'].map(function (l) { return { key: l, label: l + '"' }; }), st.tubeLength, function (k) { st.tubeLength = k; renderForm(); renderOut(); }),
        'Distance from your line to the plant, plus a little slack.'));
      form.appendChild(pair3);
      form.appendChild(field('Zones like this', num('zones', 1, 1000),
        'Repeats everything above - leave at 1 for a single zone.'));
      var styles = [{ key: 'auto', label: 'Recommended (' + STYLE_LABEL[c.style] + ')' }].concat(['asm', 'mod', 'netbow'].filter(function (k) { return k !== c.style; }).map(function (k) { return { key: k, label: STYLE_LABEL[k] }; }));
      form.appendChild(field('How to water each plant', chips('style', styles, st.style, function (k) { st.style = k; renderForm(); renderOut(); })));
    }

    function want(r) {
      if (prices[r.sku] !== undefined || pending[r.sku]) return;
      pending[r.sku] = 1;
      fetch('/products/' + r.handle + '.js').then(function (res) { return res.ok ? res.json() : null; }).then(function (p) {
        (p && p.variants || []).forEach(function (v) { if (v.sku) prices[v.sku] = v.price; });
        if (prices[r.sku] === undefined) prices[r.sku] = null;
      }).catch(function () { prices[r.sku] = null; }).then(function () { delete pending[r.sku]; renderOut(); });
    }

    function renderOut() {
      var P = planDrip(qs, byKey, st), total = 0, priced = true;
      out.textContent = '';
      var head = el('div', 'hbi-qs__head');
      head.appendChild(el('div', 'hbi-qs__eyebrow', 'Our suggestion'));
      var title = { asm: 'Ready-made drip stake assemblies', mod: (P.points <= 2 ? '2-way' : '4-way') + ' MOD assemblies with nipple emitters', netbow: 'NetBow rings on barbed hub emitters' }[P.style];
      head.appendChild(el('div', 'hbi-qs__title', title));
      head.appendChild(el('div', 'hbi-qs__sub', P.points + ' drip point' + (P.points > 1 ? 's' : '') + ' per plant for ' + P.c.label + ' · ' + P.plants + ' plants' + (P.benchWidth ? ' · ' + P.benchWidth + ' ft bench width' : '') + (P.zones > 1 ? ' · x ' + P.zones + ' zones' : '')));
      out.appendChild(head);

      var stats = el('div', 'hbi-qs__stats');
      [[(P.plants * P.points).toLocaleString(), 'drip points'], [P.perPlantGph.toFixed(2), 'GPH per plant'], [P.gpm.toFixed(2), 'GPM total'],
       [P.shotMin < 1 ? Math.round(P.shotMin * 60) + ' s' : P.shotMin.toFixed(1) + ' min', 'for a 3% shot']].forEach(function (s) {
        var b = el('div', 'hbi-qs__stat'); b.appendChild(el('b', null, s[0])); b.appendChild(el('span', null, s[1])); stats.appendChild(b);
      });
      out.appendChild(stats);

      var list = el('ul', 'hbi-qs__list');
      P.lines.forEach(function (l) {
        want(l.r);
        var price = prices[l.r.sku];
        if (typeof price === 'number') total += price * l.qty; else priced = false;
        var li = el('li');
        var name = el('span', 'hbi-qs__line-name');
        var pk = l.r.pack || 1;
        // Packs are spelled out - "1 x drip stake assembly" read as ONE
        // assembly when it's really one pack of 25 (Dustin, 2026-10-09).
        name.appendChild(el('b', null, pk > 1 ? l.qty + (l.qty === 1 ? ' pack' : ' packs') + ' × ' : l.qty + ' × '));
        name.appendChild(document.createTextNode(l.r.name + (l.r.detail ? ' - ' + l.r.detail : '')));
        if (pk > 1) name.appendChild(el('span', 'hbi-qs__pack', ' (' + pk.toLocaleString() + ' per pack · ' + (pk * l.qty).toLocaleString() + ' ' + plural(l.r.unit || 'unit', pk * l.qty) + ')'));
        li.appendChild(name);
        li.appendChild(el('span', 'hbi-qs__line-price', typeof price === 'number' ? money(price * l.qty) : ''));
        li.appendChild(el('span', 'hbi-qs__line-why', l.why));
        list.appendChild(li);
      });
      out.appendChild(list);
      // Max ~2 notes at once, priority-sorted (plan part 3) - warnings
      // (flow/pressure limits, needs action) before tips (nice-to-know),
      // stable within each level so otherwise-equal notes keep their
      // original order. A build that trips more than 2 still only shows
      // the 2 most important - better than a wall of text.
      var NOTE_RANK = { warn: 0, tip: 1 };
      P.notes.slice().sort(function (a, b) {
        return (NOTE_RANK[a.level] != null ? NOTE_RANK[a.level] : 1) - (NOTE_RANK[b.level] != null ? NOTE_RANK[b.level] : 1);
      }).slice(0, 2).forEach(function (n) {
        var box = el('div', 'hbi-qs__note hbi-qs__note--' + n.level, n.text);
        out.appendChild(box);
      });

      // Build-savings tiers (config.discounts, display only) - tell the
      // customer up front what this suggested build would save.
      var tiers = (config.discounts && config.discounts.tiers || []).slice().sort(function (a, b) { return a.min - b.min; });
      if (tiers.length && priced && total) {
        var cur = null, nxt = null;
        tiers.forEach(function (t) { if (total >= t.min * 100) cur = t; else if (!nxt) nxt = t; });
        var save = el('div', 'hbi-qs__save' + (cur ? ' hbi-qs__save--on' : ''));
        // per-unit, rounded down - same as Shopify's checkout math
        var saveCents = 0;
        if (cur) P.lines.forEach(function (l) { var pr = prices[l.r.sku]; if (typeof pr === 'number') saveCents += l.qty * Math.floor(pr * cur.percent / 100); });
        if (cur) save.innerHTML = '<b>' + cur.percent + '% OFF</b> This build saves ' + money(saveCents) + (nxt ? ' &middot; add ' + money(nxt.min * 100 - total) + ' more for ' + nxt.percent + '%' : '');
        else save.innerHTML = '<b>BUILD &amp; SAVE</b> Add ' + money(nxt.min * 100 - total) + ' more to this build to save ' + nxt.percent + '%';
        out.appendChild(save);
      }
      var actions = el('div', 'hbi-qs__actions');
      var btn = el('button', 'hbi-qs__apply', 'Use this build' + (priced && total ? ' · ' + money(total) : ''));
      btn.type = 'button';
      btn.addEventListener('click', function () {
        var selected = {}, picks = {}, firstSection = null;
        P.lines.forEach(function (l) {
          selected[l.r.sku] = (selected[l.r.sku] || 0) + l.qty;
          if (l.r.groupKey) picks[l.r.groupKey] = l.r.values;
          if (!firstSection) firstSection = l.r.section;
        });
        document.dispatchEvent(new CustomEvent('hbi:groupedset', { detail: { familyKey: config.family && config.family.key, selected: selected, picks: picks, openSection: firstSection } }));
        var box = document.getElementById(root.getAttribute('data-buybox-id'));
        if (box) {
          var hh = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
          window.scrollTo({ top: Math.max(0, box.getBoundingClientRect().top + window.scrollY - hh - 12), behavior: 'smooth' });
        }
        done.textContent = 'Added to your build - review it on the right (or below on mobile), change anything, then add it to your cart.';
      });
      actions.appendChild(btn);
      var mailBtn = el('a', 'hbi-qs__email', 'Email this build');
      mailBtn.href = buildMailto(config, P, prices, total, priced);
      actions.appendChild(mailBtn);
      var done = el('span', 'hbi-qs__done');
      actions.appendChild(done);
      out.appendChild(actions);
      var fine = el('p', 'hbi-qs__hint');
      fine.appendChild(document.createTextNode('Suggestions are typical starting points, not rules. Shot time assumes a 3% shot of container volume.'));
      if (qs.cyclePlannerUrl) {
        fine.appendChild(document.createTextNode(' Plan your full schedule with the '));
        var a = el('a', null, 'Irrigation Cycle Planner'); a.href = qs.cyclePlannerUrl; fine.appendChild(a);
        fine.appendChild(document.createTextNode('.'));
      }
      out.appendChild(fine);
    }

    renderForm();
    renderOut();
  }

  function init() {
    var roots = document.querySelectorAll('[data-hbi-grouped-quickstart]');
    for (var i = 0; i < roots.length; i++) mount(roots[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
